import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import './AudioPlayer.css';

const SongPlayer = () => {
  const { songId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [songInfo, setSongInfo] = useState(null);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const playerRef = useRef(null);

  // Check for debug mode via URL parameter or environment variable
  const urlParams = new URLSearchParams(location.search);
  const isDebugMode = urlParams.get('debug') === 'true' ||
                      process.env.REACT_APP_DEBUG_MODE === 'true' ||
                      process.env.NODE_ENV === 'development';

  const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL || 'https://your-bucket-name.s3.amazonaws.com';

  const songDatabase = {
    song1: { name: 'Dave Grohl', s3Key: 'songs/dave_grohl.mp3' },
    song2: { name: 'Sample Audio 2', s3Key: 'songs/Guido_Vitale.wav' },
    song3: { name: 'Sample Audio 3', s3Key: 'songs/Harry_Belafonte.wav' },
  };

  useEffect(() => {
    if (songId && songDatabase[songId]) {
      setSongInfo(songDatabase[songId]);
      setLoading(false);

      // Check if we can autoplay (desktop usually allows it)
      // On mobile, we'll show a play button instead
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setShowPlayButton(true);
      }
    } else {
      setError('Song not found');
      setLoading(false);
    }
  }, [songId]);

  const handlePlayClick = () => {
    setShowPlayButton(false);
    // The audio will autoplay once the button is clicked
    if (playerRef.current && playerRef.current.audio && playerRef.current.audio.current) {
      playerRef.current.audio.current.play();
    }
  };

  if (loading) {
    return <div className="audio-player loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="audio-player error">
        <h2>Error: {error}</h2>
        <Link to="/">Go back to QR Generator</Link>
      </div>
    );
  }

  const audioUrl = `${S3_BUCKET_URL}/${songInfo.s3Key}`;

  return (
    <div className="audio-player" style={{ backgroundImage: 'url(/dave_grohl_bg01.png)' }}>
      <div className="player-container">
        {showPlayButton && (
          <button
            onClick={handlePlayClick}
            className="mobile-play-button"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#000',
              border: '3px solid #ffcc00',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#ffcc00">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        )}
        <div className="h5-player-wrapper" style={{ opacity: showPlayButton ? 0.3 : 1 }}>
          <AudioPlayer
            ref={playerRef}
            autoPlay={!showPlayButton}
            autoPlayAfterSrcChange={!showPlayButton}
            src={audioUrl}
            onPlay={() => {
              console.log('Playing:', songInfo.name);
              // Track play event in Google Analytics
              if (window.gtag) {
                console.log('Sending GA4 play_song event for:', songInfo.name);
                window.gtag('event', 'play_song', {
                  song_id: songId,
                  song_name: songInfo.name,
                  custom_dimension: 'audio_player'
                });
              } else {
                console.warn('Google Analytics not loaded');
              }
            }}
            onPause={() => console.log('Paused:', songInfo.name)}
            onEnded={() => {
              console.log('Ended:', songInfo.name);
              // Track song completion
              if (window.gtag) {
                window.gtag('event', 'complete_song', {
                  song_id: songId,
                  song_name: songInfo.name
                });
              }
            }}
            onError={(e) => console.error('Audio error:', e)}
            onLoadStart={() => console.log('Loading started:', audioUrl)}
            onCanPlay={() => console.log('Can play:', songInfo.name)}
            showJumpControls={false}
            showSkipControls={false}
            showDownloadProgress={true}
            showFilledVolume={false}
            customProgressBarSection={[
              'CURRENT_TIME',
              'PROGRESS_BAR',
              'DURATION',
            ]}
            customControlsSection={[
              'MAIN_CONTROLS',
              <div key="download" className="rhap_download-button">
                <a
                  href={audioUrl}
                  download={`${songInfo.name.replace(/\s+/g, '_')}.mp3`}
                  className="rhap_button-download"
                  aria-label="Download"
                  onClick={() => {
                    // Track download event in Google Analytics
                    if (window.gtag) {
                      window.gtag('event', 'download_song', {
                        song_id: songId,
                        song_name: songInfo.name
                      });
                    }
                  }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                </a>
              </div>,
            ]}
            volume={1.0}
            muted={false}
            preload="auto"
            crossOrigin="anonymous"
          />
        </div>

        {isDebugMode && (
          <>
            <div className="ios-notice">
              📱 <strong>iOS Users:</strong>
              <br />
              • Make sure your device volume is up and not in silent mode!
              <br />
              • Check that the ringer/silent switch is OFF (not orange)
              <br />
              • Use device volume buttons to adjust system volume
            </div>

            <div className="debug-info">
              <details>
                <summary>🔧 Debug Audio Info (Tap to expand)</summary>
                <div className="debug-content">
                  <p>Song: {songInfo.name}</p>
                  <p>File: {songInfo.s3Key}</p>
                  <p>Audio URL: <a href={audioUrl} target="_blank" rel="noopener noreferrer">Test Direct</a></p>
                  <small>Using react-h5-audio-player for cross-platform compatibility</small>
                </div>
              </details>
            </div>

            <Link to="/" className="back-link">
              ← Back to QR Generator
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default SongPlayer;